import { useState, useEffect } from "react";
import { api, apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Loader2, Trash2, AlertTriangle, KeyRound } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { clinic_name: "", contact_name: "", phone: "", email: "", password: "", fiscal_name: "", cif: "", fiscal_address: "", client_code: "", travel_cost: "", discount_percent: "", discount_note: "" };

export function ClinicDialog({ open, onOpenChange, clinic, onSaved }) {
  const isEdit = !!clinic;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newPwd, setNewPwd] = useState("");
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (open) {
      setConfirmDelete(false);
      setNewPwd("");
      setForm({
        clinic_name: clinic?.clinic_name || "", contact_name: clinic?.contact_name || "",
        phone: clinic?.phone || "", email: clinic?.email || "", password: "",
        fiscal_name: clinic?.fiscal_name || "", cif: clinic?.cif || "", fiscal_address: clinic?.fiscal_address || "",
        client_code: clinic?.client_code || "",
        travel_cost: clinic?.travel_cost != null ? String(clinic.travel_cost) : "",
        discount_percent: clinic?.discount_percent ? String(clinic.discount_percent) : "",
        discount_note: clinic?.discount_note || "",
      });
    }
  }, [open, clinic]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    setSaving(true);
    const numeric = {
      travel_cost: Number(form.travel_cost) || 0,
      discount_percent: Number(form.discount_percent) || 0,
      discount_note: form.discount_note || "",
    };
    try {
      if (isEdit) {
        await api.patch(`/clinics/${clinic.id}`, {
          email: form.email, clinic_name: form.clinic_name, contact_name: form.contact_name, phone: form.phone,
          fiscal_name: form.fiscal_name, cif: form.cif, fiscal_address: form.fiscal_address, client_code: form.client_code,
          ...numeric,
        });
        toast.success("Datos de la clínica actualizados");
      } else {
        await api.post("/clinics", { ...form, ...numeric });
        toast.success("Clínica dada de alta");
      }
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/clinics/${clinic.id}`);
      toast.success("Clínica dada de baja");
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); } finally { setDeleting(false); }
  };

  const resetPassword = async () => {
    if (newPwd.length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres"); return; }
    setResetting(true);
    try {
      await api.patch(`/clinics/${clinic.id}/password`, { new_password: newPwd });
      toast.success("Contraseña de la clínica restablecida");
      setNewPwd("");
    } catch (e) { toast.error(apiError(e)); } finally { setResetting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="clinic-dialog">
        <DialogHeader><DialogTitle className="text-[#373E45]">{isEdit ? "Editar clínica" : "Dar de alta clínica"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-[#373E45] font-semibold">Nombre de la clínica</Label>
            <Input value={form.clinic_name} onChange={set("clinic_name")} className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" data-testid="clinic-name-input" />
          </div>
          <div>
            <Label className="text-[#373E45] font-semibold">Código de cliente <span className="font-normal text-[#64748B]">(3 letras, para el nº de caso)</span></Label>
            <Input value={form.client_code} onChange={(e) => setForm({ ...form, client_code: e.target.value.toUpperCase().slice(0, 3) })} maxLength={3} placeholder={isEdit ? "" : "Se genera automáticamente"} className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498] uppercase font-bold tracking-widest w-32" data-testid="clinic-code-input" />
          </div>
          <div>
            <Label className="text-[#373E45] font-semibold">Persona de contacto</Label>
            <Input value={form.contact_name} onChange={set("contact_name")} className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" data-testid="clinic-contact-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[#373E45] font-semibold">Teléfono</Label>
              <Input value={form.phone} onChange={set("phone")} className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" data-testid="clinic-phone-input" />
            </div>
            <div>
              <Label className="text-[#373E45] font-semibold">Email</Label>
              <Input type="email" value={form.email} onChange={set("email")} className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" data-testid="clinic-email-input" />
            </div>
          </div>
          {!isEdit && (
            <div>
              <Label className="text-[#373E45] font-semibold">Contraseña inicial</Label>
              <Input type="text" value={form.password} onChange={set("password")} placeholder="Mínimo 6 caracteres" className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" data-testid="clinic-password-input" />
            </div>
          )}
          <div className="pt-3 border-t border-slate-100">
            <p className="text-xs font-bold text-[#64748B] uppercase mb-3">Datos de facturación</p>
            <div className="space-y-3">
              <div>
                <Label className="text-[#373E45] font-semibold">Nombre fiscal</Label>
                <Input value={form.fiscal_name} onChange={set("fiscal_name")} placeholder="Razón social" className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" data-testid="clinic-fiscalname-input" />
              </div>
              <div>
                <Label className="text-[#373E45] font-semibold">CIF</Label>
                <Input value={form.cif} onChange={set("cif")} placeholder="B12345678" className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" data-testid="clinic-cif-input" />
              </div>
              <div>
                <Label className="text-[#373E45] font-semibold">Dirección fiscal</Label>
                <Input value={form.fiscal_address} onChange={set("fiscal_address")} placeholder="Calle, nº, CP, ciudad" className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" data-testid="clinic-fiscaladdress-input" />
              </div>
            </div>
          </div>

          <div className="pt-3 border-t border-slate-100">
            <p className="text-xs font-bold text-[#64748B] uppercase mb-3">Particularidades de esta clínica</p>
            <div className="space-y-3">
              <div>
                <Label className="text-[#373E45] font-semibold">Coste de desplazamiento (€)</Label>
                <Input type="number" min="0" step="0.01" value={form.travel_cost} onChange={set("travel_cost")} placeholder="0.00" className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498] w-40" data-testid="clinic-travelcost-input" />
              </div>
              <div>
                <Label className="text-[#373E45] font-semibold">% de descuento promocional</Label>
                <Input type="number" min="0" max="100" step="0.01" value={form.discount_percent} onChange={set("discount_percent")} placeholder="0" className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498] w-40" data-testid="clinic-discount-input" />
              </div>
              <div>
                <Label className="text-[#373E45] font-semibold">Observaciones del descuento</Label>
                <Input value={form.discount_note} onChange={set("discount_note")} placeholder='Ej. "Para los próximos 5 estudios"' className="mt-1.5 bg-slate-50 focus-visible:ring-[#7FD498]" data-testid="clinic-discountnote-input" />
              </div>
            </div>
          </div>
        </div>

        {isEdit && (
          <div className="mt-2 pt-4 border-t border-slate-100">
            <p className="text-xs font-bold text-[#64748B] uppercase mb-2 flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5" />Restablecer contraseña</p>
            <p className="text-xs text-[#64748B] mb-2">Asigna una nueva contraseña a esta clínica (no necesitas la actual). Comunícasela para que pueda acceder.</p>
            <div className="flex gap-2">
              <Input type="text" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} placeholder="Nueva contraseña (mín. 6)" className="bg-slate-50 focus-visible:ring-[#7FD498]" data-testid="clinic-newpassword-input" />
              <Button onClick={resetPassword} disabled={resetting || !newPwd} variant="outline" className="rounded-xl border-slate-300 font-bold text-[#373E45] shrink-0" data-testid="reset-clinic-password-btn">
                {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Restablecer"}
              </Button>
            </div>
          </div>
        )}

        <DialogFooter className="flex-col gap-3 sm:flex-col">
          <Button onClick={submit} disabled={saving} className="w-full bg-[#7FD498] hover:bg-[#6BC284] text-[#373E45] font-bold rounded-xl" data-testid="save-clinic-btn">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : (isEdit ? "Guardar cambios" : "Crear clínica")}
          </Button>
          {isEdit && (
            confirmDelete ? (
              <div className="w-full rounded-xl border border-red-200 bg-red-50 p-3" data-testid="confirm-delete-box">
                <p className="text-sm font-semibold text-red-700 flex items-center gap-1.5 mb-2"><AlertTriangle className="h-4 w-4" />¿Dar de baja esta clínica? Se cancelarán sus citas pendientes.</p>
                <div className="flex gap-2">
                  <Button onClick={remove} disabled={deleting} size="sm" className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl" data-testid="confirm-delete-clinic-btn">
                    {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sí, dar de baja"}
                  </Button>
                  <Button onClick={() => setConfirmDelete(false)} size="sm" variant="outline" className="flex-1 rounded-xl border-slate-300 font-semibold">Cancelar</Button>
                </div>
              </div>
            ) : (
              <Button onClick={() => setConfirmDelete(true)} variant="outline" className="w-full rounded-xl border-red-200 text-red-600 hover:text-red-700 hover:bg-red-50 font-bold" data-testid="delete-clinic-btn">
                <Trash2 className="h-4 w-4 mr-1" />Dar de baja clínica
              </Button>
            )
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
